// ABOUTME: The object schema form values of the read filters: drafts, per-field validation and the mutation input.
// ABOUTME: Pure helpers; the server validates the expression itself (allowlist + EXPLAIN) on save.

import { ReadFilterInput } from '@src/__generated__/types';

/** A read filter as stored on the object schema. */
export interface StoredReadFilter {
    code: string;
    expression: string;
    timeoutMs?: number | null;
    description?: string | null;
    source?: string | null;
}

/** A read filter in the form: every field as typed, the timeout included. */
export interface ReadFilterDraft {
    code: string;
    expression: string;
    timeoutMs: string;
    description: string;
    /** the tenant package shipping the filter, kept as is */
    source: string;
}

/** Codes of the local validation errors, translated by useReadFilterErrorMessages. */
export type ReadFilterErrorCode =
    | 'invalid-code'
    | 'duplicate-code'
    | 'empty-expression'
    | 'expression-too-long'
    | 'invalid-timeout';

export const READ_FILTER_CODE_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;
export const READ_FILTER_EXPRESSION_MAX_LENGTH = 4000;
export const READ_FILTER_TIMEOUT_MIN_MS = 100;
export const READ_FILTER_TIMEOUT_MAX_MS = 30000;

export const toDrafts = (filters: readonly StoredReadFilter[]): ReadFilterDraft[] =>
    filters.map(filter => ({
        code: filter.code,
        expression: filter.expression,
        timeoutMs: filter.timeoutMs != null ? String(filter.timeoutMs) : '',
        description: filter.description ?? '',
        source: filter.source ?? '',
    }));

export const emptyDraft = (): ReadFilterDraft => ({
    code: '',
    expression: '',
    timeoutMs: '',
    description: '',
    source: '',
});

/** The timeout as a number, undefined when empty; NaN when it is not an integer. */
export const parseTimeout = (timeoutMs: string): number | undefined => {
    const trimmed = timeoutMs.trim();
    if (!trimmed) {
        return undefined;
    }
    return /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
};

/** The code of the filter at `index`: a valid identifier, not used by an earlier filter. */
export const validateCode = (
    drafts: readonly ReadFilterDraft[],
    index: number,
): ReadFilterErrorCode | undefined => {
    const code = drafts[index].code.trim();
    if (!READ_FILTER_CODE_PATTERN.test(code)) {
        return 'invalid-code';
    }
    return drafts.some((other, i) => i < index && other.code.trim() === code)
        ? 'duplicate-code'
        : undefined;
};

export const validateExpression = (expression: string): ReadFilterErrorCode | undefined => {
    const trimmed = expression.trim();
    if (!trimmed) {
        return 'empty-expression';
    }
    return trimmed.length > READ_FILTER_EXPRESSION_MAX_LENGTH ? 'expression-too-long' : undefined;
};

export const validateTimeout = (timeoutMs: string): ReadFilterErrorCode | undefined => {
    const timeout = parseTimeout(timeoutMs);
    return timeout !== undefined &&
        (Number.isNaN(timeout) ||
            timeout < READ_FILTER_TIMEOUT_MIN_MS ||
            timeout > READ_FILTER_TIMEOUT_MAX_MS)
        ? 'invalid-timeout'
        : undefined;
};

/** The mutation input: the whole list, which replaces the stored one. Empty fields are left out. */
export const toReadFilterInputs = (drafts: readonly ReadFilterDraft[]): ReadFilterInput[] =>
    drafts.map(draft => {
        const timeout = parseTimeout(draft.timeoutMs);
        return {
            code: draft.code.trim(),
            expression: draft.expression.trim(),
            ...(timeout !== undefined && !Number.isNaN(timeout) ? { timeoutMs: timeout } : {}),
            ...(draft.description.trim() ? { description: draft.description.trim() } : {}),
            ...(draft.source ? { source: draft.source } : {}),
        };
    });

/** Whether the drafts differ from the stored filters (order included: it is the saved order). */
export const isChanged = (
    drafts: readonly ReadFilterDraft[],
    stored: readonly StoredReadFilter[],
): boolean =>
    JSON.stringify(toReadFilterInputs(drafts)) !==
    JSON.stringify(toReadFilterInputs(toDrafts(stored)));
